import { ApiService } from '../../ApiService';
import snakecaseKeys from 'snakecase-keys';
import { UpsertApplicationDto } from './UpsertApplicationDto';
import { BulkActionResponse } from '../../../entities/BulkActionResponse';
import { SendEmailDto } from '../../dtos/EmailDto';
import { InterviewListItem } from '../../../entities/InterviewListItem';
import { PushToHrisFormValues } from '../../../containers/CandidatePage/CandidateActions/EditablePushToHrisModal';

interface RejectProps {
  applicationId: number;
  rejectReasonId: number;
  currentStageId?: number;
}

interface RejectResponse {
  hasRejectionEmailAutomation: boolean;
}

interface BulkRejectProps {
  applicationIds: number[];
  rejectReasonId: number;
}

interface PushToHrisProps {
  applicationIds: number[];
  pushToHrisFormValues?: PushToHrisFormValues;
}

export class ApplicationService {
  public static listInterviews(
    applicationId: number,
    status?: string,
    minStartDate?: Date,
  ): Promise<InterviewListItem[]> {
    return ApiService.get(
      `/api/applicant_tracking/applications/${applicationId}/interviews`,
      snakecaseKeys({ status, minStartDate: minStartDate?.toISOString() }),
    );
  }

  public static moveStage(
    applicationId: number,
    stageId?: number,
    currentStageId?: number,
  ): Promise<void> {
    const body: Record<string, number> = {};
    if (stageId != null) body.jobStageId = stageId;
    if (currentStageId != null) body.currentStageId = currentStageId;

    return ApiService.post(
      `/api/applicant_tracking/applications/${applicationId}/move`,
      Object.keys(body).length > 0 ? snakecaseKeys(body) : undefined,
    );
  }

  public static bulkMoveStage(props: {
    applicationIds: number[];
    jobStageId: number;
  }): Promise<BulkActionResponse> {
    return ApiService.post(
      `/api/applicant_tracking/applications/bulk_move`,
      snakecaseKeys(props),
    );
  }

  public static reject(props: RejectProps): Promise<RejectResponse> {
    return ApiService.post<RejectResponse>(
      `/api/applicant_tracking/applications/${props.applicationId}/reject`,
      snakecaseKeys(props),
    );
  }

  public static bulkReject(props: BulkRejectProps): Promise<void> {
    return ApiService.post<void>(
      `/api/applicant_tracking/applications/bulk_reject`,
      snakecaseKeys(props),
    );
  }

  public static unreject(applicationId: number): Promise<void> {
    return ApiService.post<void>(
      `/api/applicant_tracking/applications/${applicationId}/unreject`,
    );
  }

  public static sendEmails(
    applicationIds: number[],
    props: SendEmailDto,
  ): Promise<void> {
    if (applicationIds.length === 1) {
      return this.sendSingleEmail(applicationIds[0], props);
    }

    return this.sendBulkEmails(applicationIds, props);
  }

  private static sendSingleEmail(
    applicationId: number,
    props: SendEmailDto,
  ): Promise<void> {
    return ApiService.post<void>(
      `/api/applicant_tracking/applications/${applicationId}/send_email`,
      snakecaseKeys(props),
    );
  }

  private static sendBulkEmails(
    applicationIds: number[],
    props: SendEmailDto,
  ): Promise<void> {
    return ApiService.post<void>(
      `/api/applicant_tracking/applications/send_bulk_emails`,
      snakecaseKeys({ applicationIds, ...props }),
    );
  }

  public static upsert(
    props: UpsertApplicationDto,
    applicationId: number = null,
  ): Promise<{ id: number }> {
    if (applicationId == null) {
      return this.create(props);
    } else {
      return this.update(applicationId, props);
    }
  }

  private static create(props: UpsertApplicationDto): Promise<{ id: number }> {
    return ApiService.post<{ id: number }>(
      '/api/applicant_tracking/applications',
      this.makeParams(props),
    );
  }

  private static update(
    applicationId: number,
    props: UpsertApplicationDto,
  ): Promise<{ id: number }> {
    return ApiService.put<{ id: number }>(
      `/api/applicant_tracking/applications/${applicationId}`,
      this.makeParams(props),
    );
  }

  public static pushToHris(props: PushToHrisProps): Promise<void> {
    if (props.applicationIds.length == 1) return this.singlePushToHris(props);

    return this.bulkPushToHris(props);
  }

  private static singlePushToHris(props: PushToHrisProps): Promise<void> {
    return ApiService.post<void>(
      `/api/applicant_tracking/applications/${props.applicationIds[0]}/push_to_hris`,
      snakecaseKeys(props),
    );
  }

  private static bulkPushToHris(props: PushToHrisProps): Promise<void> {
    return ApiService.post<void>(
      `/api/applicant_tracking/applications/bulk_push_to_hris`,
      snakecaseKeys(props),
    );
  }

  private static makeParams(props: UpsertApplicationDto): object {
    return snakecaseKeys({
      ...props,
      resumeFilename: undefined,
      coverLetterFilename: undefined,
    });
  }
}
